/**
 * Production Spine API client — Phase A endpoints from /production-logs/*
 * and /production/sales-order-progress.
 *
 * Backend: backend/app/api/routers/production_logs.py (Sprint 0136).
 */
import apiClient from '@/shared/api/client'

// ─── Status workflow ────────────────────────────────────────────────

export const LOG_STATUSES = [
  'DRAFT', 'IN_PROGRESS', 'SUBMITTED', 'SUPERVISOR_REVIEW',
  'APPROVED', 'REJECTED', 'LOCKED',
] as const
export type LogStatus = (typeof LOG_STATUSES)[number]

export const PRODUCTION_TYPES = [
  'MAKE_TO_ORDER', 'MAKE_TO_STOCK', 'SAMPLE', 'REWORK', 'TRIAL',
] as const
export type ProductionType = (typeof PRODUCTION_TYPES)[number]

export const CONFIDENCE_LEVELS = [
  'EXACT', 'ESTIMATED', 'MANUAL', 'DATA_NOT_AVAILABLE',
] as const
export type Confidence = (typeof CONFIDENCE_LEVELS)[number]

export const STATUS_COLOR: Record<LogStatus, string> = {
  DRAFT: 'default',
  IN_PROGRESS: 'processing',
  SUBMITTED: 'cyan',
  SUPERVISOR_REVIEW: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
  LOCKED: 'purple',
}

export const PRODUCTION_TYPE_COLOR: Record<ProductionType, string> = {
  MAKE_TO_ORDER: 'blue',
  MAKE_TO_STOCK: 'cyan',
  SAMPLE: 'gold',
  REWORK: 'orange',
  TRIAL: 'magenta',
}

// ─── Types ──────────────────────────────────────────────────────────

export interface AutoFetchInput {
  plant_id: string
  department_code: string
  machine_id?: string
  shift_id?: string
  log_date: string  // YYYY-MM-DD
  count_id?: string
  yarn_type?: string
  sales_order_id?: string
  sales_order_line_id?: string
  production_campaign_id?: string
  production_plan_id?: string
  lot_no?: string
}

export interface AutoFetchPreview {
  sales_order_id?: string
  customer_id?: string
  production_type?: ProductionType
  yarn_type?: string
  count_id?: string
  production_campaign_id?: string
  production_plan_id?: string
  planned_output_kg?: number
  expected_output_kg?: number
  worker_allocation_id?: string
  actual_headcount?: number
  planned_headcount?: number
  opening_stock_kg?: number
  opening_stock_source_log_id?: string
  display_data?: Record<string, unknown>
  _sources: Record<string, string>
}

export interface SOProgressRow {
  sales_order_id: string
  so_no: string
  customer_name: string | null
  count_description: string | null
  yarn_type: string | null
  production_type: string | null
  ordered_qty_kg: number
  produced_qty_kg: number
  packed_qty_kg: number
  dispatched_qty_kg: number
  pending_production_kg: number
  pending_dispatch_kg: number
  delivery_date: string | null
  delay_days: number | null
  status: string
  last_production_at: string | null
}

export interface OpeningStockSource {
  log_id: string
  stored_opening_stock_kg: number | null
  resolved_opening_stock_kg: number | null
  source: string
  confidence: Confidence
  source_log_id: string | null
}

export interface TransitionResponse {
  id: string
  status: LogStatus
  approved_at: string | null
  locked_at: string | null
  review_flags: { field: string; reason: string }[] | null
}

// ─── API surface ────────────────────────────────────────────────────

export const productionSpineApi = {
  /** Non-mutating preview of what auto-fetch WOULD populate. */
  previewAutofetch: async (body: AutoFetchInput) =>
    (await apiClient.post<AutoFetchPreview>(
      '/production-logs/preview-autofetch', body,
    )).data,

  /** Apply auto-fetch into an existing DRAFT log. */
  autofetchIntoLog: async (log_id: string, overwrite: boolean = false) =>
    (await apiClient.post<{
      id: string
      log_no: string
      sources: Record<string, string>
      applied_fields: string[]
      review_flags: { field: string; reason: string }[] | null
    }>(
      `/production-logs/${log_id}/autofetch?overwrite=${overwrite}`,
    )).data,

  /** Move log through status workflow. */
  transitionLog: async (log_id: string, body: {
    new_status: LogStatus
    reason?: string
  }) =>
    (await apiClient.post<TransitionResponse>(
      `/production-logs/${log_id}/transition`, body,
    )).data,

  /** Explain where this log's opening_stock_kg came from. */
  openingStockSource: async (log_id: string) =>
    (await apiClient.get<OpeningStockSource>(
      `/production-logs/${log_id}/opening-stock-source`,
    )).data,

  /** SO progress dashboard — produced/packed/dispatched/pending per SO. */
  listSOProgress: async (params: {
    plant_id: string
    customer_id?: string
    delayed_only?: boolean
  }) =>
    (await apiClient.get<SOProgressRow[]>(
      '/production/sales-order-progress', { params },
    )).data,

  /** Force-recompute a single SO's counters from current logs/FG lots. */
  recomputeSO: async (sales_order_id: string) =>
    (await apiClient.post<SOProgressRow>(
      `/production/sales-orders/${sales_order_id}/recompute`,
    )).data,
}
